import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { PROGRAMS } from "../data/content";
import ContactSplit from "../components/sections/ContactSplit";
import { MaskedLine, MaskedLineInView } from "../components/MaskedLine";

export default function ProgramDetailPage() {
  const { slug } = useParams();
  const idx = PROGRAMS.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/programs" replace />;
  const program = PROGRAMS[idx];
  const next = PROGRAMS[(idx + 1) % PROGRAMS.length];

  return (
    <main data-testid={`page-program-${slug}`}>
      {/* Hero */}
      <section className="ground-paper pt-[104px] pb-14 md:pt-[132px] md:pb-20">
        <div className="wrap">
          <Link to="/programs" className="stamp inline-flex items-center gap-2 hover:text-[color:var(--pink)]">
            <ArrowLeft size={13} /> All programs
          </Link>

          <div className="grid lg:grid-cols-12 gap-x-10 gap-y-14 mt-12 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-8">
                <span className="series-stamp"><span>Program {program.number}</span></span>
                <span className="stamp-dim">3 papers · 1 idea</span>
              </div>
              <h1 className="h-hero" style={{ fontSize: "clamp(48px, 8vw, 128px)" }} data-testid="program-title">
                <MaskedLine>{program.title.split(" ").slice(0, Math.ceil(program.title.split(" ").length / 2)).join(" ")}</MaskedLine>
                <MaskedLine delay={0.12}>
                  <span className="claim">{program.title.split(" ").slice(Math.ceil(program.title.split(" ").length / 2)).join(" ")}</span>
                </MaskedLine>
              </h1>
              <p className="aside mt-10 max-w-[560px]">{program.tagline}</p>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="frame aspect-[5/6]" style={{ transform: "rotate(-1.5deg)" }}>
                <img src={program.image} alt={program.title} className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-4 left-4 bg-[color:var(--pink)] text-[#0E1330] px-4 py-2">
                <p className="stamp" style={{ fontSize: 10.5 }}>Fig. 01 — the practice</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="ground-highlighter sec-pad">
        <div className="wrap grid lg:grid-cols-12 gap-x-10 gap-y-14">
          <div className="lg:col-span-4">
            <span className="series-stamp"><span>The Thesis</span></span>
            <h2 className="h-section mt-8" style={{ fontSize: "clamp(30px, 4vw, 56px)" }}>
              <MaskedLineInView>The premise</MaskedLineInView>
              <MaskedLineInView delay={0.08}><span className="claim">behind the work.</span></MaskedLineInView>
            </h2>
          </div>
          <p className="lg:col-span-8 font-aside" style={{ fontSize: "clamp(22px, 2.2vw, 30px)", lineHeight: 1.4 }}>
            {program.summary}
          </p>
        </div>
      </section>

      {/* What you'll learn */}
      <section className="ground-ink text-[color:var(--paper)] sec-pad">
        <div className="wrap grid lg:grid-cols-12 gap-x-10 gap-y-14">
          <div className="lg:col-span-4">
            <span className="series-stamp on-ink"><span>The Practice</span></span>
            <h2 className="h-section mt-8">
              <MaskedLineInView>Four pillars.</MaskedLineInView>
              <MaskedLineInView delay={0.08}><span className="claim">Practised.</span></MaskedLineInView>
            </h2>
          </div>
          <ol className="lg:col-span-8">
            {program.learn.map((item, i) => (
              <li key={i} className="grid grid-cols-[auto_1fr] gap-x-10 py-8 md:py-10 border-b border-[color:rgba(239,231,214,0.2)]">
                <span className="font-mono font-semibold text-[color:var(--pink)] pt-2" style={{ fontSize: 13, letterSpacing: "0.14em" }}>
                  0{i + 1}
                </span>
                <p className="font-aside text-[color:var(--paper)]" style={{ fontSize: "clamp(20px, 1.9vw, 26px)", lineHeight: 1.4 }}>
                  {item}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Method */}
      <section className="ground-lab sec-pad">
        <div className="wrap max-w-[900px]">
          <span className="series-stamp green"><span>Methodology</span></span>
          <p className="h-tile mt-10" style={{ fontSize: "clamp(28px, 3.6vw, 52px)", lineHeight: 1.1 }} data-testid="program-method">
            &ldquo;{program.method}&rdquo;
          </p>
        </div>
      </section>

      {/* Assessment examples — only for programmes that define them */}
      {program.assessments && (
        <section className="ground-highlighter sec-pad" data-testid="program-assessments">
          <div className="wrap grid lg:grid-cols-12 gap-x-10 gap-y-14">
            <div className="lg:col-span-4">
              <span className="series-stamp yellow"><span>Assessment</span></span>
              <h2 className="h-section mt-8">
                <MaskedLineInView>How we check</MaskedLineInView>
                <MaskedLineInView delay={0.08}><span className="claim">it landed.</span></MaskedLineInView>
              </h2>
            </div>
            <ol className="lg:col-span-8 border-t-[1.5px] border-[color:var(--ink)]">
              {program.assessments.map((item, i) => (
                <li key={i} data-testid={`assessment-${i}`} className="grid grid-cols-[auto_1fr] gap-x-10 py-8 border-b-[1.5px] border-[color:var(--ink)]">
                  <span className="font-mono font-semibold text-[color:var(--signal)] pt-2" style={{ fontSize: 13, letterSpacing: "0.14em" }}>
                    0{i + 1}
                  </span>
                  <p className="font-aside" style={{ fontSize: "clamp(19px, 1.8vw, 24px)", lineHeight: 1.45 }}>
                    {item}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Outcomes */}
      <section className="ground-margin sec-pad">
        <div className="wrap">
          <div className="max-w-[700px]">
            <span className="series-stamp"><span>Outcomes</span></span>
            <h2 className="h-section mt-8">
              <MaskedLineInView>What changes.</MaskedLineInView>
              <MaskedLineInView delay={0.08}><span className="claim">After.</span></MaskedLineInView>
            </h2>
          </div>
          <div className="mt-14 border-t-[1.5px] border-[color:var(--ink)] grid grid-cols-1 md:grid-cols-3">
            {program.outcomes.map((o, i) => (
              <div key={i} className={`p-8 md:p-10 border-b-[1.5px] border-[color:var(--ink)] md:border-b-0 ${i > 0 ? "md:border-l-[1.5px]" : ""}`}>
                <span className="stamp">0{i + 1}</span>
                <p className="font-aside mt-6 text-[20px]" style={{ lineHeight: 1.5 }}>{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Next */}
      <section className="ground-paper py-16">
        <div className="wrap flex flex-wrap items-baseline justify-between gap-6 border-t-[1.5px] border-[color:var(--ink)] pt-8">
          <div>
            <p className="stamp">Next program</p>
            <Link to={`/programs/${next.slug}`} data-testid="next-program-link" className="font-hook-black text-[#0E1330] hover:text-[color:var(--pink)] transition-colors inline-flex items-center gap-3 mt-3" style={{ fontSize: "clamp(28px, 4vw, 56px)", letterSpacing: "-0.02em", lineHeight: 1 }}>
              {next.title} <ArrowUpRight size={26} />
            </Link>
          </div>
          <Link to="/programs" className="arrow-link">All programs <ArrowUpRight size={14} /></Link>
        </div>
      </section>

      <ContactSplit />
    </main>
  );
}
